<script setup lang="ts">
import FormColorPicker from '@/components/ui/ColorPicker/ColorPicker.vue'
import type {
  LoyaltyMerchantFormColors,
  LoyaltyMerchantFormMedia,
  LoyaltyMerchantFormMediaImages,
  LoyaltyMerchantFormTexts
} from '@/composablestypes/global'
import Mockup from '@/components/Mockup.vue'
import useMedia from '@/composables/media'

import type { ImageFile } from '@/types/types'

import { Swiper, SwiperSlide } from 'swiper/vue'
import { Autoplay } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/autoplay'

const props = defineProps<{
  merchantMedias: LoyaltyMerchantFormMedia
  merchantColors: LoyaltyMerchantFormColors
  merchantTexts: LoyaltyMerchantFormTexts
}>()

const { isDefaultLogo } = useMedia()
const { hasPermissionBySlug } = useAccess()
const { getContrast } = useColor()
const { t } = useI18n()

const isOpen = ref(false)
const colors = computed(() => props.merchantColors)
const currentCover = ref<LoyaltyMerchantFormMediaImages>()

const coverImages = ref<ImageFile[]>([])
const coverInitialImages = ref<ImageFile[]>([])
const coverDeletedImages = ref<number[]>([])

const tabletImages = computed(() => {
  return Object.values(props.merchantMedias).filter((image) => {
    return image.name.includes('cover')
  })
})

const covers = computed(() =>
  Object.keys(props.merchantMedias)
    .filter((cover) => cover.includes('cover'))
    .reduce((obj, key) => {
      obj[key] = props.merchantMedias[key]
      return obj
    }, {} as LoyaltyMerchantFormMedia)
)

const selectCover = (item: LoyaltyMerchantFormMediaImages) => {
  currentCover.value = item
  const existing = defaultPictureUrl(item)
  coverInitialImages.value = existing ? [{ id: Date.now(), path: existing, name: item.name }] : []
  coverImages.value = []
  coverDeletedImages.value = []
  isOpen.value = true
}

const defaultPictureUrl = (item: LoyaltyMerchantFormMediaImages) => {
  if (item.pictureUrl !== null) {
    return item.pictureUrl
  }

  return item.defaultPictureUrl
}

const merchantLogo = computed(() => {
  const logoUrl = props.merchantMedias.logo.pictureUrl
  return isDefaultLogo(logoUrl) ? null : logoUrl
})

const acceptImageUploaded = () => {
  const coverName = currentCover.value?.name
  const uploaded = coverImages.value[0]
  if (coverName && uploaded && covers.value[coverName]) {
    covers.value[coverName].pictureUrl = uploaded.path
    covers.value[coverName].sizeOk = true
  }
  isOpen.value = false
}

// Same as PongoNative's darkenColor, used for the home button border
const darkenColor = (hexColor: string, amount: number) => {
  const channels = [1, 3, 5].map((start) => Math.max(0, parseInt(hexColor.slice(start, start + 2), 16) - amount))
  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`
}

const hasPendingUpload = computed(() => coverImages.value.length > 0)

const canSeeAndUpdateIdentity = computed(
  () => hasPermissionBySlug('global-configuration.update') && hasPermissionBySlug('global-configuration.read')
)
</script>
<template>
  <div class="flex flex-col gap-5">
    <Card v-if="canSeeAndUpdateIdentity">
      <CardHeader>
        <CardTitle>{{ t('pages.settings.merchantForm.color.subtitle') }}</CardTitle>
      </CardHeader>
      <CardContent class="grid grid-cols-6 gap-6">
        <div v-for="(color, index) in colors" :key="index" class="col-span-6 sm:col-span-2">
          <FormColorPicker :id="`color${index}`" v-model="color.value" :label="color.label" />
        </div>
      </CardContent>
    </Card>
    <div class="flex w-full flex-col gap-5">
      <div class="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <Card class="xl:col-span-1">
          <CardHeader>
            <CardTitle>{{ t('pages.loyaltyPage.loyaltyMerchantPage.tablet.homeImages') }}</CardTitle>
          </CardHeader>
          <CardContent class="grid h-full grid-cols-2 grid-rows-2 gap-5">
            <div v-for="cover in covers" :key="cover.name" class="flex w-full flex-1 flex-col items-center">
              <img
                class="rounded-sm p-0 md:p-10 xl:p-0"
                :src="cover.pictureUrl ? cover.pictureUrl : cover.defaultPictureUrl"
                alt=""
              />
              <Button variant="outline" class="mt-5 gap-2" @click="selectCover(cover)">
                <BaseIcon name="HeroArrowPathIconSolid" class="h-5 w-5" />
                <p>{{ t('pages.loyaltyPage.loyaltyMerchantPage.tablet.editButton') }}</p>
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card class="hidden sm:flex xl:col-span-2">
          <CardHeader>
            <CardTitle>{{ t('pages.loyaltyPage.loyaltyMerchantPage.tablet.preview') }}</CardTitle>
          </CardHeader>
          <CardContent class="flex h-full flex-col justify-center">
            <div class="mx-auto w-full max-w-2xl">
              <Mockup>
                <div
                  class="rounded-x flex aspect-[1180/820] w-full flex-col [container-type:size]"
                  :style="`background-color: ${colors.background.value};`"
                >
                  <!-- Sizes below are PongoNative's home screen values on a 1180x820pt iPad (10th gen), as cqw / cqh -->
                  <div class="flex h-full w-full px-[5.42cqw]">
                    <div class="flex h-full min-w-0 flex-1 flex-col justify-between">
                      <div class="flex min-h-0 flex-1 flex-col justify-center">
                        <!-- Same box as PongoNative's home logo: 128pt high on an 820pt-high screen -->
                        <img
                          v-if="merchantLogo"
                          class="mb-[2.44cqh] max-h-[15.61cqh] max-w-full shrink-0 self-start object-contain object-left-top"
                          :src="merchantLogo"
                        />
                        <div class="font-serif leading-[1.33]" :style="`color: ${colors.text.value}`">
                          <h2 class="mb-[2.44cqh] text-[4.39cqh]">
                            {{ merchantTexts.homeTextsFields.homeTitle.value }}
                          </h2>
                          <h3 class="mb-[7.8cqh] text-[5.85cqh] font-bold">
                            {{ merchantTexts.homeTextsFields.homeSubtitle.value }}
                          </h3>
                        </div>
                        <p
                          class="tablet-button flex h-[9.76cqh] w-max max-w-full items-center rounded-full border-x border-b-[0.61cqh] px-[4.66cqw] font-serif text-[4.39cqh] font-semibold whitespace-nowrap shadow-[0_2px_3px_rgba(0,0,0,0.3)]"
                          :style="`background: ${colors.button.value}; border-color: ${darkenColor(colors.button.value, 50)}; color: ${getContrast(colors.button.value)}`"
                        >
                          {{ merchantTexts.homeTextsFields.homeButton.value }}
                        </p>
                      </div>
                      <div
                        class="flex items-center gap-[2.71cqw] pb-[3.9cqh] font-serif text-[1.95cqh] leading-[normal]"
                        :style="`color: ${getContrast(colors.background.value)}`"
                      >
                        <div class="flex items-center gap-[0.51cqw]">
                          <svg class="size-[2.93cqh]" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                            <path
                              d="M11.7913 7.89237V3.00896C11.7913 2.26727 11.1901 1.66602 10.4484 1.66602C9.70672 1.66602 9.10547 2.26727 9.10547 3.00896V7.03777"
                              stroke-linecap="round"
                            />
                            <path
                              d="M13.6223 10.7008L15.4354 4.65716C15.6469 3.95219 15.2412 3.21038 14.5335 3.00818C13.8293 2.80698 13.0958 3.2174 12.8989 3.92284L11.791 7.89289"
                              stroke-linecap="round"
                            />
                            <path
                              d="M6.34878 7.70736L7.2284 9.65857C7.53233 10.3327 7.23398 11.1258 6.56102 11.4325C5.88418 11.7409 5.08555 11.4405 4.77985 10.7624L3.90023 8.81123C3.5963 8.137 3.89465 7.34395 4.5676 7.03727C5.24445 6.72881 6.04308 7.02927 6.34878 7.70736Z"
                              stroke-linecap="round"
                            />
                            <path
                              d="M9.76512 10.2174C9.9357 9.74445 9.91562 9.22378 9.70895 8.76545L8.8147 6.78173C8.5142 6.11522 7.72923 5.8199 7.06395 6.12308C6.38636 6.43188 6.09816 7.23972 6.4273 7.90767L6.54034 8.13704"
                              stroke-linecap="round"
                            />
                            <path
                              d="M11.5478 14.7303L11.9579 13.9103C11.9986 13.8286 11.9557 13.7299 11.8682 13.7042L8.90923 12.8339C8.31609 12.6595 7.93229 12.0862 7.99701 11.4713C8.07042 10.774 8.69098 10.2653 9.38915 10.3303L13.3791 10.7015C13.3791 10.7015 16.5533 11.1898 15.4546 14.364C14.3558 17.5383 13.9896 18.637 11.5478 18.637C9.96073 18.637 7.64111 18.637 7.64111 18.637H7.39694C5.44158 18.637 3.85646 17.0519 3.85646 15.0966L3.73438 8.25977"
                              stroke-linecap="round"
                            />
                          </svg>
                          <p>{{ t('pages.loyaltyPage.loyaltyMerchantPage.tablet.mockup.employeeSpace') }}</p>
                        </div>
                        <div class="flex items-center gap-[0.51cqw]">
                          <svg
                            class="size-[2.93cqh]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          >
                            <path
                              d="M12.5024 9.13041L14.5511 9.66154C14.8157 9.73015 15.002 9.96978 14.9929 10.243C14.8267 15.2323 12.0005 16.0003 12.0005 16.0003C12.0005 16.0003 9.17426 15.2323 9.00813 10.243C8.99903 9.96978 9.1853 9.73015 9.44993 9.66154L11.4986 9.13041C11.8278 9.04507 12.1732 9.04507 12.5024 9.13041Z"
                            />
                            <path
                              d="M2 7.99949L11.7317 3.13365C11.9006 3.0492 12.0994 3.04919 12.2683 3.13365L22 7.99949"
                            />
                            <path d="M20 11V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19V11" />
                          </svg>
                          <p>{{ t('pages.loyaltyPage.loyaltyMerchantPage.tablet.mockup.cgu') }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="h-full w-1/3 shrink-0 pl-[1.36cqw]">
                      <Swiper
                        class="h-full w-full"
                        :direction="'vertical'"
                        :slides-per-view="1.8"
                        :centered-slides="true"
                        :loop="true"
                        :speed="2000"
                        :autoplay="{
                          disableOnInteraction: false,
                          delay: 1500
                        }"
                        :allow-touch-move="false"
                        :modules="[Autoplay]"
                      >
                        <SwiperSlide
                          v-for="(image, index) in tabletImages"
                          :key="index"
                          class="opacity-50 transition-opacity duration-[2000ms] [&.swiper-slide-active]:opacity-100"
                        >
                          <div class="h-full py-[1.95cqh]">
                            <img
                              :src="image.pictureUrl ? image.pictureUrl : image.defaultPictureUrl"
                              class="h-full w-full rounded-[2.93cqh] object-cover"
                            />
                          </div>
                        </SwiperSlide>
                      </Swiper>
                    </div>
                  </div>
                </div>
              </Mockup>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>

    <BaseModal
      :is-open="isOpen"
      :is-disabled="!hasPendingUpload"
      :heading-title="t('pages.loyaltyPage.loyaltyMerchantPage.tablet.modal.title')"
      :link-text="t('pages.loyaltyPage.loyaltyMerchantPage.tablet.modal.submit')"
      @close="isOpen = false"
      @click="acceptImageUploaded"
    >
      <div v-if="currentCover">
        <label class="mb-2 block text-sm font-medium text-foreground">{{ currentCover.label }}</label>
        <ImageUploaderNew
          :images="coverImages"
          :initial-images="coverInitialImages"
          :deleted-images="coverDeletedImages"
          single-image
          crop
          @update:images="coverImages = $event"
          @update:deleted-images="coverDeletedImages = $event"
        />
      </div>
    </BaseModal>
  </div>
</template>
<style scoped>
/* Same bounce as PongoNative's Button with hasBounce */
.tablet-button {
  animation: tablet-button-bounce 1.3s ease-in-out infinite alternate;
}

@keyframes tablet-button-bounce {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(0.95);
  }
}
</style>
